import { receiptStore } from "../receipts";
import { vendorStore } from "../vendors";
import type {
  Expense,
  ExpenseStatus,
  ExpenseStore,
  NewExpenseInput,
} from "./types";

const STORAGE_KEY = "proofly.expenses.v1";

// Bypass-mode uses a single well-known user id so tests can assert which
// rows "belong to the current user" (submittedBy === BYPASS_USER_ID).
const BYPASS_USER_ID = "00000000-0000-0000-0000-000000000000";

function readFromStorage(): Expense[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Backfill any missing Wave 11 / Wave 9 fields so older stored rows
    // keep typing (vendorId was added in Wave 9 — older rows see `null`).
    return (parsed as Partial<Expense>[]).map((row) => ({
      createdBy: null,
      submittedBy: null,
      submittedAt: null,
      approvedBy: null,
      approvedAt: null,
      rejectionReason: null,
      vendorId: null,
      ...row,
    })) as Expense[];
  } catch {
    return [];
  }
}

function writeToStorage(expenses: Expense[]): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(expenses));
}

function generateId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `e_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function deriveStatus(
  current: ExpenseStatus | undefined,
  hasFile: boolean,
): ExpenseStatus {
  if (current === "archived") return "archived";
  return hasFile ? "ready" : "needs_info";
}

export function createLocalStorageExpenseStore(): ExpenseStore {
  const listeners = new Set<() => void>();

  let snapshot: Expense[] = readFromStorage();

  function emit(): void {
    for (const listener of listeners) {
      listener();
    }
  }

  function commit(next: Expense[]): void {
    snapshot = next;
    writeToStorage(next);
    emit();
  }

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key !== STORAGE_KEY) return;
      snapshot = readFromStorage();
      emit();
    });
  }

  return {
    list(): Expense[] {
      return snapshot;
    },

    get(id: string): Expense | undefined {
      return snapshot.find((e) => e.id === id);
    },

    async add(input: NewExpenseInput): Promise<Expense> {
      const now = new Date().toISOString();
      const id = generateId();
      const hasFile = Boolean(input.file);
      const status = deriveStatus(undefined, hasFile);

      // Wave 9 — auto-link to a vendor before we persist the row. Failure
      // here is non-fatal; we surface vendorId=null and let the user link
      // manually from the detail view.
      let vendorId: string | null = null;
      if (input.vendor && input.vendor.trim()) {
        try {
          const vendor = await vendorStore.upsertByName(input.vendor);
          vendorId = vendor.id;
        } catch {
          vendorId = null;
        }
      }

      const expense: Expense = {
        id,
        vendor: input.vendor,
        vendorId,
        occurredOn: input.occurredOn,
        amountCents: input.amountCents,
        currency: input.currency ?? "USD",
        categoryId: input.categoryId ?? null,
        categoryLabel: input.categoryLabel ?? null,
        taxPolicyId: input.taxPolicyId ?? null,
        taxPolicyLabel: input.taxPolicyLabel ?? null,
        taxAmountCents: 0,
        deductibleAmountCents: null,
        businessPurpose: input.businessPurpose ?? null,
        status,
        employee: input.employee ?? null,
        department: input.department ?? null,
        confidenceScore: null,
        createdBy: BYPASS_USER_ID,
        submittedBy: BYPASS_USER_ID,
        submittedAt: null,
        approvedBy: null,
        approvedAt: null,
        rejectionReason: null,
        createdAt: now,
        updatedAt: now,
      };

      commit([expense, ...snapshot]);

      if (input.file) {
        try {
          await receiptStore.add({
            file: input.file,
            linkedExpenseId: id,
          });
        } catch (err) {
          const idx = snapshot.findIndex((e) => e.id === id);
          if (idx !== -1) {
            const updated: Expense = {
              ...snapshot[idx]!,
              status: "needs_info",
              updatedAt: new Date().toISOString(),
            };
            const next = snapshot.slice();
            next[idx] = updated;
            commit(next);
          }
          throw err;
        }
      }

      return expense;
    },

    async addMany(inputs: NewExpenseInput[]): Promise<Expense[]> {
      if (inputs.length === 0) return [];

      // Wave 9 — resolve vendor ids up front. Serial rather than parallel so
      // repeated rows for the same vendor don't race the local upsert cache.
      // Failures degrade to vendorId=null; the row still imports.
      const vendorIds: Array<string | null> = [];
      for (const input of inputs) {
        if (!input.vendor || !input.vendor.trim()) {
          vendorIds.push(null);
          continue;
        }
        try {
          const vendor = await vendorStore.upsertByName(input.vendor);
          vendorIds.push(vendor.id);
        } catch {
          vendorIds.push(null);
        }
      }

      // Build every Expense in-memory first so we only commit (and therefore
      // emit) once at the end. CSV imports can be hundreds of rows; thrashing
      // the listeners once per row is a guaranteed jank.
      const now = new Date().toISOString();
      const created: Expense[] = inputs.map((input, idx) => {
        const hasFile = Boolean(input.file);
        return {
          id: generateId(),
          vendor: input.vendor,
          vendorId: vendorIds[idx] ?? null,
          occurredOn: input.occurredOn,
          amountCents: input.amountCents,
          currency: input.currency ?? "USD",
          categoryId: input.categoryId ?? null,
          categoryLabel: input.categoryLabel ?? null,
          taxPolicyId: input.taxPolicyId ?? null,
          taxPolicyLabel: input.taxPolicyLabel ?? null,
          taxAmountCents: 0,
          deductibleAmountCents: null,
          businessPurpose: input.businessPurpose ?? null,
          status: deriveStatus(undefined, hasFile),
          employee: input.employee ?? null,
          department: input.department ?? null,
          confidenceScore: null,
          createdBy: BYPASS_USER_ID,
          submittedBy: BYPASS_USER_ID,
          submittedAt: null,
          approvedBy: null,
          approvedAt: null,
          rejectionReason: null,
          createdAt: now,
          updatedAt: now,
        };
      });

      // Prepend so newest imports sit at the top, mirroring `add()`.
      commit([...created, ...snapshot]);
      return created;
    },

    async update(
      id: string,
      patch: Partial<Omit<Expense, "id" | "createdAt" | "updatedAt">>,
    ): Promise<Expense> {
      const idx = snapshot.findIndex((e) => e.id === id);
      if (idx === -1) throw new Error(`Expense ${id} not found`);
      const current = snapshot[idx]!;
      const updated: Expense = {
        ...current,
        ...patch,
        id: current.id,
        createdAt: current.createdAt,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async setStatus(id: string, status: ExpenseStatus): Promise<Expense> {
      const idx = snapshot.findIndex((e) => e.id === id);
      if (idx === -1) throw new Error(`Expense ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status === status) return current;
      const updated: Expense = {
        ...current,
        status,
        updatedAt: new Date().toISOString(),
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async remove(id: string): Promise<void> {
      const next = snapshot.filter((e) => e.id !== id);
      if (next.length !== snapshot.length) commit(next);
    },

    // Wave 11 — bypass mode simulates the full state machine so E2E tests
    // can exercise submit/approve/reject without a network hop. We still
    // enforce transitions (draft/needs_info/rejected → pending, pending →
    // approved/rejected) so tests can't accidentally skip the gate.
    async submit(id: string): Promise<Expense> {
      const idx = snapshot.findIndex((e) => e.id === id);
      if (idx === -1) throw new Error(`Expense ${id} not found`);
      const current = snapshot[idx]!;
      if (!["draft", "needs_info", "rejected"].includes(current.status)) {
        throw new Error(
          `Cannot submit expense in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Expense = {
        ...current,
        status: "pending",
        submittedBy: current.submittedBy ?? BYPASS_USER_ID,
        submittedAt: now,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async approve(id: string): Promise<Expense> {
      const idx = snapshot.findIndex((e) => e.id === id);
      if (idx === -1) throw new Error(`Expense ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status !== "pending") {
        throw new Error(
          `Cannot approve expense in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Expense = {
        ...current,
        status: "approved",
        approvedBy: BYPASS_USER_ID,
        approvedAt: now,
        rejectionReason: null,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    async reject(id: string, reason: string): Promise<Expense> {
      const idx = snapshot.findIndex((e) => e.id === id);
      if (idx === -1) throw new Error(`Expense ${id} not found`);
      const current = snapshot[idx]!;
      if (current.status !== "pending") {
        throw new Error(
          `Cannot reject expense in status ${current.status}`,
        );
      }
      const now = new Date().toISOString();
      const updated: Expense = {
        ...current,
        status: "rejected",
        approvedBy: BYPASS_USER_ID,
        approvedAt: now,
        rejectionReason: reason,
        updatedAt: now,
      };
      const next = snapshot.slice();
      next[idx] = updated;
      commit(next);
      return updated;
    },

    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
